import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Slider, Stack, Typography } from '@mui/joy';
import { FaPause, FaPlay } from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import { formatDuration } from '../elevate/recordings.model';

type Props = {
  src: string;
  /** Used when the audio file doesn't report its own length. */
  fallbackDurationSeconds?: number | null;
};

export default function RecordingPlayer({ src, fallbackDurationSeconds }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(fallbackDurationSeconds || 0);
  const [dragValue, setDragValue] = useState<number | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    setCurrent(0);
    void audio.play().catch(() => setPlaying(false));
    return () => audio.pause();
  }, [src]);

  const readDuration = () => {
    const value = audioRef.current?.duration;
    setDuration(value && Number.isFinite(value) && value > 0 ? value : (fallbackDurationSeconds || 0));
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play().catch(() => setPlaying(false));
    else audio.pause();
  };

  const seek = (seconds: number) => {
    const audio = audioRef.current;
    if (audio) audio.currentTime = seconds;
    setCurrent(seconds);
    setDragValue(null);
  };

  const position = dragValue ?? current;
  const max = Math.max(duration, position, 1);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{ px: 1, py: 0.5, borderRadius: 'sm', bgcolor: 'background.level1' }}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={readDuration}
        onDurationChange={readDuration}
        onTimeUpdate={() => {
          if (dragValue == null) setCurrent(audioRef.current?.currentTime || 0);
        }}
      />
      <IconButton
        size="sm"
        variant="soft"
        color="primary"
        aria-label={playing ? 'Pause recording' : 'Play recording'}
        onClick={togglePlay}
      >
        <IconWrapper icon={playing ? FaPause : FaPlay} />
      </IconButton>
      <Typography level="body-xs" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'right' }}>
        {formatDuration(Math.floor(position))}
      </Typography>
      <Slider
        size="sm"
        aria-label="Recording position"
        min={0}
        max={max}
        step={0.1}
        value={position}
        onChange={(_, value) => setDragValue(Array.isArray(value) ? value[0] : value)}
        onChangeCommitted={(_, value) => seek(Array.isArray(value) ? value[0] : value)}
        sx={{ flex: 1, mx: 0.5 }}
      />
      <Typography level="body-xs" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 36 }}>
        {duration > 0 ? formatDuration(duration) : '—'}
      </Typography>
    </Stack>
  );
}
